import React, { useState, useCallback, ReactElement } from 'react';
import * as css from './VehicleInformation.module.scss';
import 'src/css/themes.scss';
import { IconSeeMore, IconPrint } from 'src/content/icons';
import { ConnectedVehicleRec } from 'src/models';
import { Dialog, ScrollBar } from '@framework-controls/core';
import { strings } from 'src/localization';

export interface IProps {
    connectedVehicle: ConnectedVehicleRec;
    printConnectedVehicle: (filter: string) => void;
}

const VehicleInformation = (props: IProps) => {
    const [infoOpen, setInfoOpen] = useState(false);

    const toggleInfo = useCallback(() => {
        setInfoOpen(!infoOpen);
    }, [infoOpen]);

    const printCV = useCallback(() => {
        props.printConnectedVehicle('V');
    }, [props.printConnectedVehicle]);

    let header = strings.VehicleInformation.toUpperCase();
    const buildHeader = function (a, b) {
        return a + ' ' + b;
    };
    if ((props.connectedVehicle.VIN)) {
        header = buildHeader(header, ' - ');
        header = buildHeader(header, strings.VINShort);
        header = buildHeader(header, ':');
        header = buildHeader(header, props.connectedVehicle.VIN);
    }

    let buildDate: ReactElement | null = null;
    if (props.connectedVehicle.buildDate) {
        buildDate = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.ProductionDate}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.buildDate}
            </div>
        </span>;
    }

    let make: ReactElement | null = null;
    if (props.connectedVehicle.make) {
        make = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.Make}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.make}
            </div>
        </span>;
    }

    let series: ReactElement | null = null;
    if (props.connectedVehicle.series) {
        series = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.Series}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.series}
            </div>
        </span>;
    }

    let modelDesc: ReactElement | null = null;
    if (props.connectedVehicle.modelDesc) {
        modelDesc = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.Model}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.modelDesc}
            </div>
        </span>;
    }

    let modelNum: ReactElement | null = null;
    if (props.connectedVehicle.modelNum) {
        modelNum = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.ModelNumber}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.modelNum}
            </div>
        </span>;
    }

    let year: ReactElement | null = null;
    if (props.connectedVehicle.year) {
        year = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.Year}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.year}
            </div>
        </span>;
    }

    let extColor: ReactElement | null = null;
    if (props.connectedVehicle.extColor) {
        extColor = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.ColorCode}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.extColor}
            </div>
        </span>;
    }

    let intColor: ReactElement | null = null;
    if (props.connectedVehicle.intColor) {
        intColor = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.InteriorCode}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.intColor}
            </div>
        </span>;
    }

    let marketName: ReactElement | null = null;
    if (props.connectedVehicle.marketName) {
        marketName = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.MarketName}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.marketName}
            </div>
        </span>;
    }

    let bodyStyle: ReactElement | null = null;
    if (props.connectedVehicle.bodyStyle) {
        bodyStyle = <span className={css.infoBlock}>
            <div className={css.infoLit}>
                {strings.BodyStyle}
            </div>
            <div className={css.infoData}>
                {props.connectedVehicle.bodyStyle}
            </div>
        </span>;
    }

    let serialNumber: ReactElement | null = null;
    if (props.connectedVehicle.serialNum) {
        serialNumber =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.SerialNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.serialNum}
                </div>
            </span>;
    }

    let orderNum: ReactElement | null = null;
    if (props.connectedVehicle.orderNum) {
        orderNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.OrderNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.orderNum}
                </div>
            </span>;
    }

    let prodPoint: ReactElement | null = null;
    if (props.connectedVehicle.prodPoint) {
        prodPoint =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ProductionPoint}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.prodPoint}
                </div>
            </span>;
    }

    let prodPlant: ReactElement | null = null;
    if (props.connectedVehicle.prodPlant) {
        prodPlant =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ProductionPlant}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.prodPlant}
                </div>
            </span>;
    }

    let prodArea: ReactElement | null = null;
    if (orderNum || prodPoint || prodPlant) {
        prodArea =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {orderNum}
                    {prodPoint}
                    {prodPlant}
                </div>
            </div>;
    }

    let airConditioning: ReactElement | null = null;
    if (props.connectedVehicle.airConditioning) {
        airConditioning =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.AirConditioning}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.airConditioning}
                </div>
            </span>;
    }

    let powerSteering: ReactElement | null = null;
    if (props.connectedVehicle.powerSteering) {
        powerSteering =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.PowerSteering}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.powerSteering}
                </div>
            </span>;
    }

    let turbo: ReactElement | null = null;
    if (props.connectedVehicle.turbo) {
        turbo =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Turbo}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.turbo}
                </div>
            </span>;
    }

    let licenseNum: ReactElement | null = null;
    if (props.connectedVehicle.licenseNum) {
        licenseNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.LicenseNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.licenseNum}
                </div>
            </span>;
    }

    let newUsed: ReactElement | null = null;
    if (props.connectedVehicle.newUsed) {
        newUsed =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.NewUsed}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.newUsed}
                </div>
            </span>;
    }

    let chassisNum: ReactElement | null = null;
    if (props.connectedVehicle.chassisNum) {
        chassisNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ChassisNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.chassisNum}
                </div>
            </span>;
    }

    let keyNum: ReactElement | null = null;
    if (props.connectedVehicle.keyNum) {
        keyNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.KeyNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.keyNum}
                </div>
            </span>;
    }

    let secondKeyNum: ReactElement | null = null;
    if (props.connectedVehicle.secondKeyNum) {
        secondKeyNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.SecondKeyNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.secondKeyNum}
                </div>
            </span>;
    }

    let keylessNum: ReactElement | null = null;
    if (props.connectedVehicle.keylessNum) {
        keylessNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.KeylessNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.keylessNum}
                </div>
            </span>;
    }

    let keychipNum: ReactElement | null = null;
    if (props.connectedVehicle.keychipNum) {
        keychipNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.KeychipNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.keychipNum}
                </div>
            </span>;
    }

    let alarmNum: ReactElement | null = null;
    if (props.connectedVehicle.alarmNum) {
        alarmNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.AlarmNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.alarmNum}
                </div>
            </span>;
    }

    let frontSafetyRating: ReactElement | null = null;
    if (props.connectedVehicle.frontSafetyRating) {
        frontSafetyRating =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.FrontSafetyRating}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.frontSafetyRating}
                </div>
            </span>;
    }

    let rearSafetyRating: ReactElement | null = null;
    if (props.connectedVehicle.rearSafetyRating) {
        rearSafetyRating =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.RearSafetyRating}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.rearSafetyRating}
                </div>
            </span>;
    }

    let driverSafetyRating: ReactElement | null = null;
    if (props.connectedVehicle.driverSafetyRating) {
        driverSafetyRating =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DriverSafetyRating}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.driverSafetyRating}
                </div>
            </span>;
    }

    let passengerSafetyRating: ReactElement | null = null;
    if (props.connectedVehicle.passengerSafetyRating) {
        passengerSafetyRating =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.PassengerSafetyRating}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.passengerSafetyRating}
                </div>
            </span>;
    }

    let rolloverSafetyRating: ReactElement | null = null;
    if (props.connectedVehicle.rolloverSafetyRating) {
        rolloverSafetyRating =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.RolloverSafetyRating}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.rolloverSafetyRating}
                </div>
            </span>;
    }

    let overallSafetyRating: ReactElement | null = null;
    if (props.connectedVehicle.overallSafetyRating) {
        overallSafetyRating =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.OverallSafetyRating}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.overallSafetyRating}
                </div>
            </span>;
    }


    let ratings: ReactElement | null = null;
    if (frontSafetyRating || rearSafetyRating || driverSafetyRating || passengerSafetyRating || rolloverSafetyRating ||
        overallSafetyRating) {
        ratings =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {frontSafetyRating}
                    {rearSafetyRating}
                    {driverSafetyRating}
                    {passengerSafetyRating}
                    {rolloverSafetyRating}
                    {overallSafetyRating}
                </div>
            </div>;
    }

    let seatingCapacity: ReactElement | null = null;
    if (props.connectedVehicle.seatingCapacity) {
        seatingCapacity =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.SeatingCapacity}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.seatingCapacity}
                </div>
            </span>;
    }

    let weightRating : ReactElement | null = null;
    if (props.connectedVehicle.weightRating) {
        weightRating =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.GrossVehicleWeight}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.weightRating}
                </div>
            </span>;
    }

    let weight: ReactElement | null = null;
    if (props.connectedVehicle.weight) {
        weight =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.VehicleWeight}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.weight}
                </div>
            </span>;
    }

    let sizes: ReactElement | null = null;
    if (seatingCapacity) {
        sizes =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {seatingCapacity}
                    {weightRating}
                    {weight}
                </div>
            </div>;
    }

    let engineNum: ReactElement | null = null;
    if (props.connectedVehicle.engineNum) {
        engineNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.EngineNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.engineNum}
                </div>
            </span>;
    }

    let engineConfig: ReactElement | null = null;
    if (props.connectedVehicle.engineConfig) {
        engineConfig =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.EngineConfiguration}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.engineConfig}
                </div>
            </span>;
    }

    let engineSize: ReactElement | null = null;
    if (props.connectedVehicle.engineSize) {
        engineSize =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.EngineSize}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.engineSize}
                </div>
            </span>;
    }

    let engineType: ReactElement | null = null;
    if (props.connectedVehicle.engineType) {
        engineType =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.EngineType}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.engineType}
                </div>
            </span>;
    }

    let engineCylinders: ReactElement | null = null;
    if (props.connectedVehicle.engineCylinders) {
        engineCylinders =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.EngineCylinders}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.engineCylinders}
                </div>
            </span>;
    }

    let engine: ReactElement | null = null;
    if (engineNum || engineConfig || engineSize || engineType || engineCylinders) {
        engine =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {engineNum}
                    {engineConfig}
                    {engineSize}
                    {engineType}
                    {engineCylinders}
                </div>
            </div>;
    }

    let baseMSRP: ReactElement | null = null;
    if (props.connectedVehicle.baseMSRP) {
        baseMSRP =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.BaseMSRP}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.baseMSRP}
                </div>
            </span>;
    }

    let optionMSRP: ReactElement | null = null;
    if (props.connectedVehicle.optionMSRP) {
        optionMSRP =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.OptionMSRP}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.optionMSRP}
                </div>
            </span>;
    }

    let totalMSRP: ReactElement | null = null;
    if (props.connectedVehicle.totalMSRP) {
        totalMSRP =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.TotalMSRP}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.totalMSRP}
                </div>
            </span>;
    }

    let freight: ReactElement | null = null;
    if (props.connectedVehicle.freight) {
        freight =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Freight}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.freight}
                </div>
            </span>;
    }

    let modelPhaseCode: ReactElement | null = null;
    if (props.connectedVehicle.modelPhaseCode) {
        modelPhaseCode =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ModelPhaseCode}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.modelPhaseCode}
                </div>
            </span>;
    }

    let amounts: ReactElement | null = null;
    if (baseMSRP || optionMSRP || totalMSRP || freight || modelPhaseCode) {
        amounts =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {baseMSRP}
                    {optionMSRP}
                    {totalMSRP}
                    {freight}
                    {modelPhaseCode}
                </div>
            </div>;
    }

    let fuelCode: ReactElement | null = null;
    if (props.connectedVehicle.fuelCode) {
        fuelCode =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.FuelCode}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.fuelCode}
                </div>
            </span>;
    }

    let fuelType: ReactElement | null = null;
    if (props.connectedVehicle.fuelType) {
        fuelType =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.FuelType}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.fuelType}
                </div>
            </span>;
    }

    let fuelCapacity: ReactElement | null = null;
    if (props.connectedVehicle.fuelCapacity) {
        fuelCapacity =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.FuelCapacity}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.fuelCapacity}
                </div>
            </span>;
    }

    let fuel: ReactElement | null = null;
    if (fuelCode || fuelType || fuelCapacity) {
        fuel =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {fuelCode}
                    {fuelType}
                    {fuelCapacity}
                </div>
            </div>;
    }

    let transType: ReactElement | null = null;
    if (props.connectedVehicle.trans) {
        transType =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Transmission}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.trans}
                </div>
            </span>;
    }

    let transCode: ReactElement | null = null;
    if (props.connectedVehicle.transCode) {
        transCode =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.TransmissionCode}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.transCode}
                </div>
            </span>;
    }

    let transDesc: ReactElement | null = null;
    if (props.connectedVehicle.transDesc) {
        transDesc =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.TransmissionDescription}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.transDesc}
                </div>
            </span>;
    }

    let transmission: ReactElement | null = null;
    if (transType || transCode || transDesc) {
        transmission =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {transType}
                    {transCode}
                    {transDesc}
                </div>
            </div>;
    }

    let stockId: ReactElement | null = null;
    if (props.connectedVehicle.stockId) {
        stockId =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.StockId}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.stockId}
                </div>
            </span>;
    }

    let custNum: ReactElement | null = null;
    if (props.connectedVehicle.custNum) {
        custNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.CustomerNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.custNum}
                </div>
            </span>;
    }

    let salesNum: ReactElement | null = null;
    if (props.connectedVehicle.salesNum) {
        salesNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.SalesmanNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.salesNum}
                </div>
            </span>;
    }

    let mmc: ReactElement | null = null;
    if (props.connectedVehicle.mmc) {
        mmc =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ModelMaintenanceCode}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.mmc}
                </div>
            </span>;
    }

    let sellingDealerNum: ReactElement | null = null;
    if (props.connectedVehicle.sellingDealerNum) {
        sellingDealerNum =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.SellingDealerNumber}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.sellingDealerNum}
                </div>
            </span>;
    }

    let prodDate: ReactElement | null = null;
    if (props.connectedVehicle.prodDate) {
        prodDate =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ProductionDate}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.prodDate}
                </div>
            </span>;
    }

    let inSvcDate: ReactElement | null = null;
    if (props.connectedVehicle.inSvcDate) {
        inSvcDate =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.InServiceDate}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.inSvcDate}
                </div>
            </span>;
    }

    let prodHr: ReactElement | null = null;
    if (props.connectedVehicle.prodHr) {
        prodHr =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ProductionHour}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.prodHr}
                </div>
            </span>;
    }

    let delDate: ReactElement | null = null;
    if (props.connectedVehicle.delDate) {
        delDate =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DeliveryDate}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.delDate}
                </div>
            </span>;
    }

    let delOdom: ReactElement | null = null;
    if (props.connectedVehicle.delOdom) {
        delOdom =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DeliveryOdometer}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.delOdom}
                </div>
            </span>;
    }

    let demoDate: ReactElement | null = null;
    if (props.connectedVehicle.demoDate) {
        demoDate =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DemoDate}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.demoDate}
                </div>
            </span>;
    }

    let demoOdom: ReactElement | null = null;
    if (props.connectedVehicle.demoOdom) {
        demoOdom =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DemoOdometer}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.demoOdom}
                </div>
            </span>;
    }

    let factoryWarrExpDate: ReactElement | null = null;
    if (props.connectedVehicle.factoryWarrExpDate) {
        factoryWarrExpDate =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.FactoryWarrExpDate}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.factoryWarrExpDate}
                </div>
            </span>;
    }

    let factoryWarrExpMile: ReactElement | null = null;
    if (props.connectedVehicle.factoryWarrExpMile) {
        factoryWarrExpMile =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.FactoryWarrExpOdometer}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.factoryWarrExpMile}
                </div>
            </span>;
    }

    let svcDays: ReactElement | null = null;
    if (props.connectedVehicle.svcDays) {
        svcDays =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ServiceDays}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.svcDays}
                </div>
            </span>;
    }

    let teamCode: ReactElement | null = null;
    if (props.connectedVehicle.teamCode) {
        teamCode =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.TeamCode}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.teamCode}
                </div>
            </span>;
    }

    let lastActivityDate: ReactElement | null = null;
    if (props.connectedVehicle.lastActivityDate) {
        lastActivityDate =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.LastActivityDate}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.lastActivityDate}
                </div>
            </span>;
    }

    let sales: ReactElement | null = null;
    if (stockId || custNum || salesNum || mmc || sellingDealerNum || prodDate || inSvcDate || prodHr || delDate || delOdom ||
        demoDate || demoOdom || factoryWarrExpDate || factoryWarrExpMile || svcDays || teamCode || lastActivityDate) {
        sales =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {stockId}
                    {custNum}
                    {salesNum}
                    {mmc}
                    {sellingDealerNum}
                    {prodDate}
                    {inSvcDate}
                    {prodHr}
                    {delDate}
                    {delOdom}
                    {demoDate}
                    {demoOdom}
                    {factoryWarrExpDate}
                    {factoryWarrExpMile}
                    {svcDays}
                    {teamCode}
                    {lastActivityDate}
                </div>
            </div>;
    }

    let driverWindow: ReactElement | null = null;
    if (props.connectedVehicle.driverWindow) {
        driverWindow =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DriverWindowReedSensor}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.driverWindow}
                </div>
            </span>;
    }

    let passengerWindow: ReactElement | null = null;
    if (props.connectedVehicle.passengerWindow) {
        passengerWindow =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.PassengerWindowReedSensor}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.passengerWindow}
                </div>
            </span>;
    }

    let rearLeftWindow: ReactElement | null = null;
    if (props.connectedVehicle.rearLeftWindow) {
        rearLeftWindow =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.RearLeftWindowReedSensor}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.rearLeftWindow}
                </div>
            </span>;
    }

    let rearRightWindow: ReactElement | null = null;
    if (props.connectedVehicle.rearRightWindow) {
        rearRightWindow =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.RearRightWindowReedSensor}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.rearRightWindow}
                </div>
            </span>;
    }

    let sunroof: ReactElement | null = null;
    if (props.connectedVehicle.sunroof) {
        sunroof =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.SunroofReedSensor}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.sunroof}
                </div>
            </span>;
    }

    let sensorPulses: ReactElement | null = null;
    if (driverWindow || passengerWindow || rearLeftWindow || rearRightWindow || sunroof) {
        sensorPulses =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {driverWindow}
                    {passengerWindow}
                    {rearLeftWindow}
                    {rearRightWindow}
                    {sunroof}
                </div>
            </div>;
    }

    let misc: ReactElement | null = null;
    if (airConditioning || powerSteering || turbo || licenseNum || newUsed || chassisNum || keyNum || secondKeyNum || keylessNum ||
        keychipNum || alarmNum) {
        misc =
            <div>
                <hr></hr>
                <div className={css.infoRow}>
                    {airConditioning}
                    {powerSteering}
                    {turbo}
                    {licenseNum}
                    {newUsed}
                    {chassisNum}
                    {keyNum}
                    {secondKeyNum}
                    {keylessNum}
                    {keychipNum}
                    {alarmNum}
                </div>
            </div>;
    }

    let details: ReactElement | null = null;
    if (misc || ratings || sizes || engine || amounts || fuel || transmission || sales || sensorPulses) {
        details =
            <div>
                <div className={css.infoHeader}>
                    {strings.VehicleDetails}
                </div>
                {misc}
                {ratings}
                {sizes}
                {engine}
                {amounts}
                {fuel}
                {transmission}
                {sales}
                {sensorPulses}
            </div>;
    }

    return (
        <div className={css.block}>
            <span className={css.vinBlock}>
                <div className={css.vinRow}>
                    <div className={css.statusLit}>{strings.VINShort}</div>
                    <div className={css.icon}>
                        <IconSeeMore onClick={() => toggleInfo()} />
                    </div>
                </div>
                <div className={css.statusData}>{props.connectedVehicle.VIN}</div>
            </span>
            <Dialog
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={infoOpen}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={header}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={() => toggleInfo()}
                onBackDropClick={() => toggleInfo()}
                width={'full'}
            >
                <ScrollBar scrollVertical>
                    <div className={css.headerRow}>
                        <div className={css.infoHeader}>
                            {strings.VehicleInformation}
                        </div>
                        <IconPrint className={css.printIcon} onClick={() => printCV()} />
                    </div>
                    <hr></hr>
                    <div className={css.infoRow}>
                        {buildDate}
                        {make}
                        {series}
                        {modelDesc}
                        {modelNum}
                        {year}
                        {extColor}
                        {intColor}
                        {marketName}
                        {bodyStyle}
                        {serialNumber}
                    </div>
                    {prodArea}
                    {details}
                </ScrollBar>
            </Dialog>
        </div>
    );
};

export default VehicleInformation;